import React, { useState, useEffect, useRef } from "react";
import { MdSmartToy, MdAdd, MdSearch } from "react-icons/md";
import { styles } from "../styles/AIListPage.styles";
import { getIAConfigs } from "../services/ia";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

function AIListPage({ setActiveMenu, setSelectedIA, isMobile }) {
 

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [ias, setIAs] = useState([]);
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState({});
  const [loadedImages, setLoadedImages] = useState({});
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const sortByNameAsc = (list) =>
    [...list].sort((a, b) => (a.NOME || "").localeCompare(b.NOME || ""));

  const sortByNameDesc = (list) =>
    [...list].sort((a, b) => (b.NOME || "").localeCompare(a.NOME || ""));

  useEffect(() => {
    if (!ias.length) return;

    let isMounted = true;

    const preloadImages = async () => {
      const promises = ias.map((item) => {
        return new Promise((resolve) => {
          if (!item.FOTO) {
            resolve();
            return;
          }

          const img = new Image();
          img.src = item.FOTO;
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });
      });

      await Promise.all(promises);
    };

    preloadImages();

    return () => {
      isMounted = false;
    };
  }, [ias]);

  

  // Debounce do search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchIAs = async () => {
      if (!token) return;

      setLoading(true);

      try {
        const data = await getIAConfigs();
        setIAs(data?.ias || []);
      } catch (err) {
        console.error("Erro ao buscar AIs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchIAs();
  }, [token]);

  const handleEditIA = (item) => {
    if (setSelectedIA) {
      setSelectedIA(item);
    }
    navigate("/ais/editar");
  };

  const filteredIAs = ias
    .filter((item) => {
      const text = debouncedSearch.toLowerCase();
      return (
        item.NOME?.toLowerCase().includes(text) ||
        item.FUNCAO?.toLowerCase().includes(text) ||
        item.TAG?.toLowerCase().includes(text)
      );
    })
    .sort((a, b) => {
      if (sortOrder === "asc") {
        return (a.NOME || "").localeCompare(b.NOME || "");
      } else {
        return (b.NOME || "").localeCompare(a.NOME || "");
      }
    });

  return (
    <div ref={containerRef} style={styles.page(isMobile)}>
      <div style={styles.headerRow(isMobile)}>
        <div style={styles.iconBadge(isMobile)}>
          <MdSmartToy size={isMobile ? 20 : 22} />
        </div>

        <div>
          <h2 style={styles.title(isMobile)}>Assistentes de IA</h2>
          <p style={styles.subtitle(isMobile)}>
            Gerencie seus assistentes de inteligência artificial.
          </p>
        </div>
      </div>

      <div style={styles.card(isMobile)}>
        {loading && (
          <div style={styles.loadingOverlay(isMobile)}>
            <div style={styles.spinner(isMobile)} />
          </div>
        )}

        <div style={styles.sectionHeader(isMobile)}>
          <div>
            <h3 style={styles.cardTitle(isMobile)}>Assistentes Disponíveis</h3>
            <p style={styles.cardDescription(isMobile)}>
              Visualize e gerencie todos os seus assistentes de IA.
            </p>
          </div>
        </div>

        <div style={styles.toolbarContainer(isMobile)}>
          <div style={styles.searchWrapper(isMobile)}>
            <MdSearch
              size={isMobile ? 16 : 18}
              style={styles.searchIcon(isMobile)}
            />
            <input
              type="text"
              placeholder="Pesquisar assistentes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput(isMobile)}
            />
          </div>

          <div style={styles.toolbarActions(isMobile)}>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              style={styles.sortSelect(isMobile)}
            >
              <option value="asc">A-Z</option>
              <option value="desc">Z-A</option>
            </select>

            <button
              style={styles.addButton(isMobile)}
              onClick={() => navigate("/ais/criar")}
            >
              <MdAdd
                size={isMobile ? 18 : 20}
                style={styles.addButtonIcon(isMobile)}
              />
              Criar Assistente
            </button>
          </div>
        </div>

        {filteredIAs.length > 0 ? (
          <div style={styles.iaList(isMobile)}>
            {filteredIAs.map((item) => (
              <div
                key={item.STAMP}
                style={{
                  ...styles.iaItem(isMobile),
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                }}
                onClick={() => handleEditIA(item)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: isMobile ? 48 : 56,
                    height: isMobile ? 48 : 56,
                    minWidth: isMobile ? 48 : 56,
                    flexShrink: 0,
                  }}
                >
                  {item.FOTO && !imgError[item.STAMP] ? (
                    <>
                      {!loadedImages[item.STAMP] && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: 12,
                            border: "2px solid rgba(31, 58, 112, 0.1)",
                            background:
                              "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
                            backgroundSize: "200% 100%",
                            animation: "shimmer 1.2s infinite",
                          }}
                        />
                      )}

                      <img
                        src={item.FOTO}
                        alt={item.NOME}
                        style={{
                          ...styles.iaPhoto(isMobile),
                          position: "absolute",
                          inset: 0,
                          opacity: loadedImages[item.STAMP] ? 1 : 0,
                          transition: "opacity 0.25s ease",
                        }}
                        onLoad={() =>
                          setLoadedImages((prev) => ({
                            ...prev,
                            [item.STAMP]: true,
                          }))
                        }
                        onError={() =>
                          setImgError((prev) => ({
                            ...prev,
                            [item.STAMP]: true,
                          }))
                        }
                      />
                    </>
                  ) : (
                    <div
                      style={{
                        ...styles.iaPhotoFallback(isMobile),
                        position: "absolute",
                        inset: 0,
                      }}
                    >
                      <MdSmartToy size={isMobile ? 20 : 24} />
                    </div>
                  )}
                </div>

                <div style={styles.iaContent(isMobile)}>
                  <div style={styles.iaHeader(isMobile)}>
                    <div style={{ minWidth: 0 }}>
                      <h4 style={styles.iaTitle(isMobile)}>{item.NOME}</h4>
                      <p style={styles.iaFunction(isMobile)}>
                        {item.FUNCAO === "apolices"
                          ? "Apólices"
                          : item.FUNCAO === "chatbot"
                          ? "Chatbot"
                          : item.FUNCAO}
                      </p>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <span style={styles.tagBadge(isMobile)}>
                        {item.TAG || "Sem tag"}
                      </span>
                    </div>
                  </div>

                  <p style={styles.iaDescription(isMobile)}>
                    {item.DESCRICAO || "Sem descrição"}
                  </p>
                </div>

                <span style={styles.iaMeta(isMobile)}>
                  {new Date(item.CRIADO_EM).toLocaleDateString("pt-PT").replace(/\//g, "-")}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState(isMobile)}>
            <MdSmartToy size={48} style={{ color: "#cbd5e1" }} />
            <p style={styles.emptyMessage}>Nenhum assistente encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIListPage;
